import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { restartableTask, timeout } from 'ember-concurrency';
import { Request } from '@warp-drive/ember';
import { t, type IntlService } from 'ember-intl';
import Drawer, { DrawerBody, DrawerFooter, DrawerHeader } from 'tio-ui/components/drawer';
import Button from 'tio-ui/components/button';
import SearchInput from 'tio-ui/components/search-input';
import FilterTabs from 'tio-ui/components/filter-tabs';
import Text from 'tio-ui/components/text';
import { PhFaders } from 'ember-phosphor-icons';
import { lpnCatalogQuery } from 'tio-data/requests/lpn-page';
import type { LpnPageExtension as LpnPage } from 'tio-data/data/resources/lpn-page.ext';
import { lpnListingFor, passesLpnSavings, type LpnListing } from 'tio-common/utils/lpn/listing';
import LpnFilterFields, {
  EMPTY_LPN_FILTERS,
  type LpnFilterKey,
  type LpnFilterValues,
} from 'tio-employee/components/lpn/filter-fields';
import LpnSchoolRow from 'tio-employee/components/lpn/school-row';
import LpnSchoolDetail from 'tio-employee/components/lpn/school-detail';

type DrawerView = 'list' | 'filters' | 'detail';
type SchoolsTab = 'all' | 'recommended';

const SEARCH_DEBOUNCE_MS = 300;

interface LpnSchoolsDrawerSignature {
  Args: {
    /** Whether the drawer is showing; it stays mounted while closed. */
    isOpen: boolean;
    /** Called when the employee closes the drawer, after its search, filters and tab reset. */
    onClose: () => void;
    /** The viewer's company setting; decides overrides, visibility and recommendations. */
    companySettingId: string;
    /**
     * Restricts the list to these institution identifiers (`dapip_<id>` / `tas_<id>`), for employees
     * whose programs only allow curated schools. null lists every school the company offers.
     */
    institutionIdentifiers: readonly string[] | null;
  };
}

/** Browse, search and filter the LPN schools available to the employee, from the TA dashboard. */
export default class LpnSchoolsDrawer extends Component<LpnSchoolsDrawerSignature> {
  @service declare intl: IntlService;

  @tracked view: DrawerView = 'list';
  /** null until the employee picks a tab; the list then defaults by whether anything is recommended. */
  @tracked chosenTab: SchoolsTab | null = null;
  @tracked searchText = '';
  @tracked appliedSearch = '';
  @tracked draftFilters: LpnFilterValues = EMPTY_LPN_FILTERS;
  @tracked appliedFilters: LpnFilterValues = EMPTY_LPN_FILTERS;
  @tracked selected: LpnListing | null = null;

  @cached
  get catalogQuery() {
    // Savings run client-side (passesLpnSavings) so company overrides are honoured.
    const { savings: _savings, ...serverFilters } = this.appliedFilters;
    return lpnCatalogQuery(this.args.companySettingId, {
      ...serverFilters,
      search: this.appliedSearch,
    });
  }

  /** A search or filter is narrowing the catalog. */
  get isNarrowed() {
    return Boolean(this.appliedSearch.trim()) || this.activeFilterCount > 0;
  }

  get activeFilterCount() {
    return Object.values(this.appliedFilters).reduce((sum, keys) => sum + keys.length, 0);
  }

  get filtersLabel() {
    return this.activeFilterCount
      ? this.intl.t('lpn.schools_drawer.filters_count', { count: this.activeFilterCount })
      : this.intl.t('lpn.schools_drawer.filters');
  }

  /** A new search shows every match, so any tab pick is cleared. */
  debounceSearch = restartableTask(async (value: string) => {
    await timeout(SEARCH_DEBOUNCE_MS);
    this.appliedSearch = value;
    this.chosenTab = null;
  });

  updateSearch = (value: string) => {
    this.searchText = value;
    void this.debounceSearch.perform(value);
  };

  clearSearch = () => {
    void this.debounceSearch.cancelAll();
    this.searchText = '';
    this.appliedSearch = '';
  };

  chooseTab = (tab: string) => {
    this.chosenTab = tab === 'recommended' ? 'recommended' : 'all';
  };

  showFilters = () => {
    this.draftFilters = this.appliedFilters;
    this.view = 'filters';
  };

  updateDraftFilter = (key: LpnFilterKey, keys: string[]) => {
    this.draftFilters = { ...this.draftFilters, [key]: keys };
  };

  clearDraftFilters = () => {
    this.draftFilters = EMPTY_LPN_FILTERS;
  };

  applyFilters = () => {
    this.appliedFilters = this.draftFilters;
    this.chosenTab = null;
    this.view = 'list';
  };

  cancelFilters = () => {
    this.view = 'list';
  };

  showDetail = (listing: LpnListing) => {
    this.selected = listing;
    this.view = 'detail';
  };

  showList = () => {
    this.view = 'list';
  };

  /** Each opening starts fresh: no leftover search, filters, tab or selected school. */
  close = () => {
    void this.debounceSearch.cancelAll();
    this.view = 'list';
    this.chosenTab = null;
    this.searchText = '';
    this.appliedSearch = '';
    this.draftFilters = EMPTY_LPN_FILTERS;
    this.appliedFilters = EMPTY_LPN_FILTERS;
    this.selected = null;
    this.args.onClose();
  };

  <template>
    <Drawer
      @isOpen={{@isOpen}}
      @onClose={{this.close}}
      @placement="right"
      @size="xl"
      @manageHistory={{false}}
      @ariaLabel={{t "lpn.schools_drawer.title"}}
      data-test-lpn-schools-drawer
    >
      <DrawerHeader>
        {{#if (eq this.view "filters")}}
          <div class="tio-row-between w-full pr-8">
            <h2>{{t "lpn.schools_drawer.filters"}}</h2>
            <Button
              @appearance="link"
              @size="sm"
              @label={{t "lpn.schools_drawer.clear_all"}}
              {{on "click" this.clearDraftFilters}}
              data-test-lpn-schools-drawer-clear-filters
            />
          </div>
        {{else}}
          <div class="tio-col-extra-tight pr-8">
            <h2>{{t "lpn.schools_drawer.title"}}</h2>
            <Text @tag="p" @variant="muted" @role="body-secondary">
              {{t "lpn.schools_drawer.subtitle"}}
            </Text>
          </div>
        {{/if}}
      </DrawerHeader>
      <DrawerBody>
        {{#if (eq this.view "filters")}}
          <LpnFilterFields @values={{this.draftFilters}} @onChange={{this.updateDraftFilter}} />
        {{else if (eq this.view "detail")}}
          {{#if this.selected}}
            <LpnSchoolDetail @listing={{this.selected}} @onBack={{this.showList}} />
          {{/if}}
        {{else}}
          <div class="tio-col gap-4">
            <div class="tio-row gap-2">
              <SearchInput
                class="flex-1"
                @placeholder={{t "lpn.schools_drawer.search_placeholder"}}
                @value={{this.searchText}}
                @debounce={{0}}
                @onChange={{this.updateSearch}}
                @onClear={{this.clearSearch}}
              />
              <Button
                @appearance="outlined"
                @label={{this.filtersLabel}}
                @icon={{PhFaders}}
                {{on "click" this.showFilters}}
                data-test-lpn-schools-drawer-filters
              />
            </div>
            <Request @query={{this.catalogQuery}}>
              <:loading>
                <Text @tag="p" @variant="muted" @role="body-secondary" role="status">
                  {{t "lpn.schools_drawer.loading"}}
                </Text>
              </:loading>
              <:error>
                <Text
                  @tag="p"
                  @variant="error"
                  @role="body-secondary"
                  role="alert"
                  data-test-lpn-schools-drawer-error
                >
                  {{t "lpn.schools_drawer.load_failed"}}
                </Text>
              </:error>
              <:content as |document|>
                <LpnSchoolsList
                  @pages={{document.data}}
                  @companySettingId={{@companySettingId}}
                  @institutionIdentifiers={{@institutionIdentifiers}}
                  @savings={{this.appliedFilters.savings}}
                  @chosenTab={{this.chosenTab}}
                  @isNarrowed={{this.isNarrowed}}
                  @onChooseTab={{this.chooseTab}}
                  @onSelect={{this.showDetail}}
                />
              </:content>
            </Request>
          </div>
        {{/if}}
      </DrawerBody>
      {{#if (eq this.view "filters")}}
        <DrawerFooter>
          <div class="tio-row-end gap-2 w-full">
            <Button
              @appearance="outlined"
              @label={{t "common.cancel"}}
              {{on "click" this.cancelFilters}}
              data-test-lpn-schools-drawer-cancel-filters
            />
            <Button
              @intent="primary"
              @label={{t "lpn.schools_drawer.apply_filters"}}
              {{on "click" this.applyFilters}}
              data-test-lpn-schools-drawer-apply-filters
            />
          </div>
        </DrawerFooter>
      {{/if}}
    </Drawer>
  </template>
}

interface LpnSchoolsListSignature {
  Args: {
    pages: readonly LpnPage[];
    companySettingId: string;
    institutionIdentifiers: readonly string[] | null;
    savings: readonly string[];
    /** The employee's pick; null lets the list choose a default. */
    chosenTab: SchoolsTab | null;
    /** A search or filter is active, so results span every school rather than just recommendations. */
    isNarrowed: boolean;
    onChooseTab: (tab: string) => void;
    onSelect: (listing: LpnListing) => void;
  };
}

class LpnSchoolsList extends Component<LpnSchoolsListSignature> {
  @service declare intl: IntlService;

  @cached
  get listings() {
    const allowed = this.args.institutionIdentifiers
      ? new Set(this.args.institutionIdentifiers)
      : null;
    return this.args.pages
      .map((page) => lpnListingFor(page, this.args.companySettingId))
      .filter((listing) => listing.isVisible)
      .filter((listing) => passesLpnSavings(listing.page, listing.setting, this.args.savings))
      .filter((listing) => !allowed || allowed.has(listing.page.institutionIdentifier ?? ''))
      .sort((a, b) => (a.page.schoolName ?? '').localeCompare(b.page.schoolName ?? ''));
  }

  @cached
  get recommended() {
    return this.listings.filter((listing) => listing.isEmployerRecommended);
  }

  @cached
  get others() {
    return this.listings.filter((listing) => !listing.isEmployerRecommended);
  }

  /**
   * Employer Recommended when the company recommends anything, otherwise All Schools. A search or
   * filter shows All Schools, so a match is never hidden behind the recommended tab.
   */
  get activeTab(): SchoolsTab {
    if (this.args.chosenTab) return this.args.chosenTab;
    return !this.args.isNarrowed && this.recommended.length ? 'recommended' : 'all';
  }

  get emptyMessageKey() {
    if (this.args.isNarrowed) return 'lpn.schools_drawer.empty';
    if (this.activeTab === 'recommended') return 'lpn.schools_drawer.empty_recommended';
    return 'lpn.schools_drawer.empty_catalog';
  }

  get showOthers() {
    return this.activeTab === 'all' && this.others.length > 0;
  }

  get hasVisibleSchools() {
    return this.recommended.length > 0 || this.showOthers;
  }

  get tabs() {
    return [
      { id: 'all', label: this.intl.t('lpn.schools_drawer.tab_all') },
      { id: 'recommended', label: this.intl.t('lpn.schools_drawer.tab_recommended') },
    ];
  }

  <template>
    <div class="tio-col gap-4" data-test-lpn-schools-list>
      <FilterTabs
        @tabs={{this.tabs}}
        @activeId={{this.activeTab}}
        @onSelect={{@onChooseTab}}
        @showCounts={{false}}
      />
      {{#if this.hasVisibleSchools}}
        {{#if this.recommended.length}}
          <section class="tio-col gap-2" data-test-lpn-schools-recommended>
            <p class="tio-text-xs-semibold uppercase tracking-wide tio-fg-muted">
              {{t "lpn.schools_drawer.recommended_heading"}}
            </p>
            {{#each this.recommended as |listing|}}
              <LpnSchoolRow @listing={{listing}} @onSelect={{@onSelect}} />
            {{/each}}
          </section>
        {{/if}}
        {{#if this.showOthers}}
          <section class="tio-col gap-2" data-test-lpn-schools-others>
            <p class="tio-text-xs-semibold uppercase tracking-wide tio-fg-muted">
              {{t "lpn.schools_drawer.more_heading"}}
            </p>
            {{#each this.others as |listing|}}
              <LpnSchoolRow @listing={{listing}} @onSelect={{@onSelect}} />
            {{/each}}
          </section>
        {{/if}}
      {{else}}
        <Text @tag="p" @variant="muted" @role="body-secondary" data-test-lpn-schools-empty>
          {{t this.emptyMessageKey}}
        </Text>
      {{/if}}
    </div>
  </template>
}
